import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import VideoCard from "@/components/feature/VideoCard";
import ArticleCard from "@/components/feature/ArticleCard";
import PostRow from "@/components/feature/PostRow";
import { videos } from "@/mocks/videos";
import { articles } from "@/mocks/articles";
import { posts } from "@/mocks/posts";
import usePageMeta from "@/hooks/usePageMeta";

type TabKey = "all" | "videos" | "articles" | "posts";

const suggestions = ["羽毛", "新手", "边界", "道具", "放松", "爆笑"];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    setInput(keyword);
    setTab("all");
  }, [keyword]);

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 挠脚心网站` : "搜索 - 挠脚心网站",
    keyword
      ? `在挠脚心网站中搜索“${keyword}”相关的挠痒视频、趣味文章与社区话题。`
      : "搜索挠脚心网站的视频、文章与社区话题，快速找到你感兴趣的趣味内容。",
    "站内搜索,挠痒视频搜索,趣味文章搜索,社区话题"
  );

  const results = useMemo(() => {
    const q = keyword.toLowerCase();
    if (!q) {
      return { videos: [], articles: [], posts: [] };
    }

    const matchedVideos = videos.filter((item) =>
      [item.title, item.description, item.author, item.category, ...item.tags]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );

    const matchedArticles = articles.filter((item) =>
      [
        item.title,
        item.excerpt,
        item.author,
        item.tag,
        ...item.tags,
        ...item.content.map((block) => ("text" in block ? block.text : "")),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );

    const matchedPosts = posts.filter((item) =>
      [item.title, item.excerpt, item.author, ...item.content].join(" ").toLowerCase().includes(q)
    );

    return { videos: matchedVideos, articles: matchedArticles, posts: matchedPosts };
  }, [keyword]);

  const total = results.videos.length + results.articles.length + results.posts.length;

  const tabs: Array<{ key: TabKey; label: string; count: number }> = [
    { key: "all", label: "全部", count: total },
    { key: "videos", label: "视频", count: results.videos.length },
    { key: "articles", label: "文章", count: results.articles.length },
    { key: "posts", label: "帖子", count: results.posts.length },
  ];

  const submit = () => {
    const q = input.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const showVideos = (tab === "all" || tab === "videos") && results.videos.length > 0;
  const showArticles = (tab === "all" || tab === "articles") && results.articles.length > 0;
  const showPosts = (tab === "all" || tab === "posts") && results.posts.length > 0;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          crumbs={[{ label: "首页", to: "/" }, { label: "搜索结果" }]}
          kicker="Search"
          title={keyword ? `“${keyword}” 的搜索结果` : "站内搜索"}
          description={
            keyword
              ? `共找到 ${total} 条与关键词相关的内容，覆盖视频、文章与社区帖子。`
              : "输入关键词，一次搜索视频、文章与社区话题。"
          }
        />

        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            className="flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center"
          >
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md bg-background-100 px-3 py-2.5">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="试试搜索：羽毛、新手、边界…"
                aria-label="搜索关键词"
                className="w-full min-w-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          {!keyword ? (
            <div className="mt-6">
              <p className="text-xs text-foreground-500">试试这些关键词</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {suggestions.map((item) => (
                  <Link
                    key={item}
                    to={`/search?q=${encodeURIComponent(item)}`}
                    className="rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50"
                  >
                    #{item}
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <EmptyState
                  icon="ri-search-eye-line"
                  title="还没有输入关键词"
                  description="搜索会同时覆盖挠痒视频、趣味文章和社区话题，输入任意一个词试试看。"
                  actionLabel="回到首页"
                  actionTo="/"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-background-200 pb-3">
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {tabs.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setTab(item.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
                        tab === item.key
                          ? "bg-background-50 font-medium text-foreground-950"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {item.label}
                      <span className="ml-1 text-[11px] text-foreground-400">{item.count}</span>
                    </button>
                  ))}
                </div>
                <span className="ml-auto text-xs text-foreground-400">前端模拟搜索 · 演示数据</span>
              </div>

              {total === 0 ? (
                <div className="mt-6">
                  <EmptyState
                    icon="ri-emotion-sad-line"
                    title={`没有找到与“${keyword}”相关的内容`}
                    description="换个说法试试，或者从下面这些关键词里挑一个。"
                    actionLabel="返回首页"
                    actionTo="/"
                  />
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {suggestions.map((item) => (
                      <Link
                        key={item}
                        to={`/search?q=${encodeURIComponent(item)}`}
                        className="rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50"
                      >
                        #{item}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-8">
                  {showVideos && (
                    <section>
                      <div className="mb-4 flex items-center justify-between">
                        <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                            <i className="ri-play-circle-line text-sm"></i>
                          </span>
                          相关视频
                          <span className="text-xs font-normal text-foreground-400">
                            {results.videos.length}
                          </span>
                        </h2>
                        <Link
                          to="/videos"
                          className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-foreground-900"
                        >
                          浏览全部视频
                        </Link>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {results.videos.map((video) => (
                          <VideoCard key={video.id} video={video} />
                        ))}
                      </div>
                    </section>
                  )}

                  {showArticles && (
                    <section>
                      <div className="mb-4 flex items-center justify-between">
                        <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                            <i className="ri-article-line text-sm"></i>
                          </span>
                          相关文章
                          <span className="text-xs font-normal text-foreground-400">
                            {results.articles.length}
                          </span>
                        </h2>
                        <Link
                          to="/articles"
                          className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-foreground-900"
                        >
                          浏览全部文章
                        </Link>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {results.articles.map((article) => (
                          <ArticleCard key={article.id} article={article} />
                        ))}
                      </div>
                    </section>
                  )}

                  {showPosts && (
                    <section>
                      <div className="mb-4 flex items-center justify-between">
                        <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                            <i className="ri-chat-smile-2-line text-sm"></i>
                          </span>
                          相关帖子
                          <span className="text-xs font-normal text-foreground-400">
                            {results.posts.length}
                          </span>
                        </h2>
                        <Link
                          to="/community"
                          className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-foreground-900"
                        >
                          进入社区
                        </Link>
                      </div>
                      <div className="flex flex-col gap-3">
                        {results.posts.map((post, index) => (
                          <PostRow key={post.id} post={post} index={index} />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}